import { useEffect, useState } from 'react';
import {
  ROLES,
  ROLE_LABEL,
  USER_STATUSES,
  USER_STATUS_LABEL,
  type Role,
  type UserStatus,
} from '@doit/shared';
import { Check, Copy, Link2, Pencil, Plus, Search, Trash2, Users } from 'lucide-react';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Alert, Button, EmptyState, Field, PageHeader, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../api/auth';
import {
  useCourses,
  useCreateInvite,
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
  useUsers,
} from '../../api/queries';
import type { Invite, UserRow } from '../../api/types';
import { useDebounced } from '../../lib/useDebounced';
import { formatDate, formatDateTime } from '../../lib/format';

const ROLE_TONE: Record<Role, 'brand' | 'ok' | 'neutral'> = {
  ADMIN: 'brand',
  MANAGER: 'ok',
  LECTURER: 'neutral',
};

const STATUS_TONE: Record<UserStatus, 'ok' | 'warn' | 'neutral'> = {
  ACTIVE: 'ok',
  INVITED: 'warn',
  DISABLED: 'neutral',
};

export function UsersPage() {
  const me = useCurrentUser();
  const toast = useToast();
  const isAdmin = me.role === 'ADMIN';

  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [invite, setInvite] = useState<{ user: string; invite: Invite } | null>(null);
  const [toDelete, setToDelete] = useState<UserRow | null>(null);

  const debouncedQ = useDebounced(q, 300);
  const { data, isLoading } = useUsers({
    q: debouncedQ || undefined,
    role: role || undefined,
    status: status || undefined,
    pageSize: 200,
  });
  const remove = useDeleteUser();
  const createInvite = useCreateInvite();

  async function regenerate(user: UserRow) {
    try {
      const result = await createInvite.mutateAsync(user.id);
      setInvite({ user: user.fullName, invite: result });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'ბმული ვერ შეიქმნა');
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.id);
      toast.success('მომხმარებელი წაიშალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
    } finally {
      setToDelete(null);
    }
  }

  return (
    <>
      <PageHeader
        title="მომხმარებლები"
        subtitle={isLoading ? 'იტვირთება…' : `${data?.total ?? 0} ჩანაწერი`}
        action={
          <Button
            icon={<Plus className="size-4" />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            მომხმარებლის დამატება
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="სახელი ან ელფოსტა…"
            className="w-full rounded-lg border border-line-strong bg-surface py-2.5 pl-9 pr-3 text-sm transition placeholder:text-ink-mute focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
          />
        </div>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="ყველა როლი"
          options={ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          placeholder="ყველა სტატუსი"
          options={USER_STATUSES.map((s) => ({ value: s, label: USER_STATUS_LABEL[s] }))}
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : (data?.items ?? []).length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState icon={<Users className="size-8" />} title="მომხმარებელი ვერ მოიძებნა" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">მომხმარებელი</th>
                  <th className="px-4 py-2.5 font-semibold">როლი</th>
                  <th className="px-4 py-2.5 font-semibold">სტატუსი</th>
                  <th className="px-4 py-2.5 font-semibold">საგნები</th>
                  <th className="px-4 py-2.5 text-right font-semibold">ჯგუფი</th>
                  <th className="px-4 py-2.5 font-semibold">ბოლო შესვლა</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((u) => (
                  <tr key={u.id} className="border-t border-line hover:bg-canvas/60">
                    <td className="px-4 py-3">
                      <span className="font-medium text-ink">{u.fullName}</span>
                      <span className="block text-[12px] text-ink-mute">{u.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={ROLE_TONE[u.role]}>{ROLE_LABEL[u.role]}</Badge>
                      {u.initials && (
                        <span className="tabular ml-1.5 text-[11px] text-ink-mute">
                          {u.initials}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[u.status]}>{USER_STATUS_LABEL[u.status]}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {u.role !== 'LECTURER' ? (
                        <span className="text-ink-mute">—</span>
                      ) : u.lecturerCourses.length === 0 ? (
                        <span className="text-[12px] text-warn" title="ჯგუფზე ვერ მიემაგრება">
                          არ აქვს
                        </span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {u.lecturerCourses.slice(0, 2).map((lc) => (
                            <Badge key={lc.course.id}>{lc.course.code}</Badge>
                          ))}
                          {u.lecturerCourses.length > 2 && (
                            <Badge tone="neutral">+{u.lecturerCourses.length - 2}</Badge>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="tabular px-4 py-3 text-right text-ink-soft">
                      {u._count.lecturerGroups || '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-mute">{formatDate(u.lastLoginAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => void regenerate(u)}
                          title="სარეგისტრაციო ბმულის შექმნა"
                          aria-label="ბმული"
                          className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
                        >
                          <Link2 className="size-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => {
                                setEditing(u);
                                setFormOpen(true);
                              }}
                              aria-label="რედაქტირება"
                              className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
                            >
                              <Pencil className="size-4" />
                            </button>
                            <button
                              onClick={() => setToDelete(u)}
                              aria-label="წაშლა"
                              disabled={u.id === me.id}
                              className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger disabled:opacity-30 disabled:hover:bg-transparent"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <UserModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        user={editing}
        onInvite={(name, inv) => setInvite({ user: name, invite: inv })}
      />

      <InviteModal data={invite} onClose={() => setInvite(null)} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        title="მომხმარებლის წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.fullName}</strong>. თუ ჯგუფებზეა მიმაგრებული, წაშლა
            დაიბლოკება — ჯერ მოხსენი მიმაგრება ან გათიშე ანგარიში.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}

// ============================================================

function UserModal({
  open,
  onClose,
  user,
  onInvite,
}: {
  open: boolean;
  onClose: () => void;
  user: UserRow | null;
  onInvite: (name: string, invite: Invite) => void;
}) {
  const toast = useToast();
  const me = useCurrentUser();
  const create = useCreateUser();
  const update = useUpdateUser();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<string>('LECTURER');
  const [phone, setPhone] = useState('');
  const [initials, setInitials] = useState('');
  const [status, setStatus] = useState<string>('INVITED');
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: courses } = useCourses();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setEmail(user?.email ?? '');
    setFullName(user?.fullName ?? '');
    setRole(user?.role ?? 'LECTURER');
    setPhone(user?.phone ?? '');
    setInitials(user?.initials ?? '');
    setStatus(user?.status ?? 'INVITED');
    setCourseIds((user?.lecturerCourses ?? []).map((lc) => lc.course.id));
  }, [open, user]);

  function toggleCourse(id: string) {
    setCourseIds((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }

  // მენეჯერს მხოლოდ ლექტორის დამატება შეუძლია
  const roleOptions = (me.role === 'ADMIN' ? ROLES : (['LECTURER'] as const)).map((r) => ({
    value: r,
    label: ROLE_LABEL[r],
  }));

  async function submit() {
    setErrors({});
    try {
      if (user) {
        await update.mutateAsync({
          id: user.id,
          data: {
            email,
            fullName,
            role,
            phone,
            initials,
            status,
            ...(role === 'LECTURER' ? { courseIds } : {}),
          },
        });
        toast.success('მონაცემები განახლდა');
      } else {
        const result = await create.mutateAsync({
          email,
          fullName,
          role,
          phone,
          initials,
          ...(role === 'LECTURER' ? { courseIds } : {}),
        });
        toast.success('მომხმარებელი შეიქმნა');
        onInvite(result.user.fullName, result.invite);
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.details ?? {});
        if (!err.details) toast.error(err.message);
      } else {
        toast.error('შენახვა ვერ მოხერხდა');
      }
    }
  }

  const busy = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user ? 'მომხმარებლის რედაქტირება' : 'ახალი მომხმარებელი'}
      description={user ? undefined : 'შექმნის შემდეგ მიიღებ სარეგისტრაციო ბმულს'}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            გაუქმება
          </Button>
          <Button size="sm" onClick={() => void submit()} loading={busy}>
            შენახვა
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="სახელი გვარი"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={errors.fullName}
          autoFocus
        />
        <Field
          label="ელფოსტა"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          placeholder="name@itstep.org"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="როლი"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            error={errors.role}
            options={roleOptions}
          />
          <Field
            label="ტელეფონი"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={errors.phone}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="ინიციალები"
            value={initials}
            onChange={(e) => setInitials(e.target.value)}
            error={errors.initials}
            placeholder="GC"
            hint="ჯგუფის კოდისთვის, ლათინურით"
          />
          {user && me.role === 'ADMIN' && (
            <Select
              label="სტატუსი"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              options={USER_STATUSES.map((s) => ({ value: s, label: USER_STATUS_LABEL[s] }))}
            />
          )}
        </div>

        {/* --- საგნები: განსაზღვრავს, რომელ ჯგუფზე მიემაგრება --- */}
        {role === 'LECTURER' && (
          <div className="space-y-1.5">
            <span className="block text-[13px] font-medium text-ink-soft">საგნები</span>
            <p className="text-[12px] text-ink-mute">
              ლექტორი მხოლოდ იმ ჯგუფზე მიემაგრება, რომლის საგანიც აქ არის მონიშნული.
            </p>
            <div className="max-h-52 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
              {(courses ?? []).length === 0 ? (
                <p className="px-2 py-3 text-[13px] text-ink-mute">საგნები ჯერ არ არის</p>
              ) : (
                (courses ?? []).map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition hover:bg-canvas"
                  >
                    <input
                      type="checkbox"
                      checked={courseIds.includes(c.id)}
                      onChange={() => toggleCourse(c.id)}
                      className="size-4 accent-brand-600"
                    />
                    <span className="min-w-0 flex-1 truncate text-ink">{c.name}</span>
                    <Badge>{c.project.code}</Badge>
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: c.direction.color ?? '#cfd4dc' }}
                      title={c.direction.name}
                    />
                  </label>
                ))
              )}
            </div>
            {courseIds.length === 0 && (
              <p className="text-[12px] text-warn">
                საგნის გარეშე ლექტორი ჯგუფზე ვერ მიემაგრება.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================================

function InviteModal({
  data,
  onClose,
}: {
  data: { user: string; invite: Invite } | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (data) setCopied(false);
  }, [data]);

  async function copy() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.invite.url);
      setCopied(true);
      toast.success('ბმული დაკოპირდა');
    } catch {
      toast.error('კოპირება ვერ მოხერხდა — მონიშნე ხელით');
    }
  }

  return (
    <Modal
      open={Boolean(data)}
      onClose={onClose}
      title="სარეგისტრაციო ბმული"
      description={data?.user}
      footer={
        <Button size="sm" onClick={onClose}>
          დახურვა
        </Button>
      }
    >
      {data && (
        <div className="space-y-4">
          <Alert kind="info">
            ბმული <strong>ერთხელ</strong> ჩანს — ბაზაში მხოლოდ ჰეშია. დაკარგვის შემთხვევაში
            შექმენი ახალი (ძველი მაშინვე გაუქმდება).
          </Alert>

          <div className="flex gap-2">
            <input
              readOnly
              value={data.invite.url}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2.5 font-mono text-[12px] text-ink"
            />
            <Button
              size="sm"
              variant={copied ? 'secondary' : 'primary'}
              icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              onClick={() => void copy()}
            >
              {copied ? 'დაკოპირდა' : 'კოპირება'}
            </Button>
          </div>

          <p className="text-[12px] text-ink-mute">
            ვადა: {formatDateTime(data.invite.expiresAt)}. ბმულის გახსნისას მომხმარებელი
            პაროლს დააყენებს და ანგარიში გააქტიურდება.
          </p>
        </div>
      )}
    </Modal>
  );
}
